import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Unlock } from 'lucide-react';
import { appendUtm } from '../utils/utm';
import { useAuth } from '../context/AuthContext';
import { OpenAccessEmailForm } from './OpenAccessEmailForm';

/** Locked-content card shown on any article a non-subscriber tries to read.
 *  Free accounts get one story a month; after that it's a paid plan. During an
 *  open-access window the copy flips to the email gate: one address, no card.
 *  Copy is intentionally direct (Ryan's phrasing). */
const PaywallCard = ({ grace, meter }) => {
  const { user } = useAuth();
  const open = !!grace?.active;
  const usedFreeRead = !!user && !!meter && meter.limit > 0 && meter.remaining === 0;

  if (open) {
    return (
      <aside
        data-testid="paywall-card"
        className="my-8 rounded-lg border-2 border-red-600/60 bg-gradient-to-b from-red-950/40 to-black/95 shadow-[0_0_40px_rgba(220,38,38,0.25)] p-6 sm:p-8"
      >
        <div className="flex items-center gap-2 text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-4">
          <Unlock className="h-3.5 w-3.5" />
          Paywall down right now
        </div>
        <h2
          className="text-white text-2xl sm:text-3xl font-black leading-tight mb-4"
          style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.01em' }}
        >
          Every story is free right now. Drop your email in and read.
        </h2>
        <p className="text-zinc-400 text-sm mb-6">
          No card. No password. Your email joins the Outlaw list and sets up your free membership,
          and the whole site opens on the spot.
        </p>
        <div className="max-w-sm">
          <OpenAccessEmailForm testid="paywall-open-access" />
        </div>
        <Link
          to={appendUtm("/login")}
          data-testid="paywall-login-link"
          className="inline-block mt-4 text-zinc-400 hover:text-white text-[11px] uppercase tracking-[0.25em] transition-colors"
        >
          Already have an account? Log in
        </Link>
      </aside>
    );
  }

  if (usedFreeRead) {
    return (
      <aside
        data-testid="paywall-card"
        className="my-8 rounded-lg border-2 border-red-600/60 bg-gradient-to-b from-red-950/40 to-black/95 shadow-[0_0_40px_rgba(220,38,38,0.25)] p-6 sm:p-8"
      >
        <div className="flex items-center gap-2 text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-4">
          <Lock className="h-3.5 w-3.5" />
          Free story used
        </div>
        <h2
          className="text-white text-2xl sm:text-3xl font-black leading-tight mb-4"
          style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.01em' }}
        >
          That's your free story for this month. The rest are for subscribers.
        </h2>
        <p className="text-zinc-400 text-sm mb-6">
          Your next free read lands on the 1st. Or go unlimited today &mdash; Monthly $13 · Annual
          $130 · Founding $249 one-off (80 places).
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to={appendUtm("/subscribe")}
            data-testid="paywall-signup-cta"
            className="inline-flex items-center justify-center px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em] transition-colors"
          >
            Choose your plan
          </Link>
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="paywall-card"
      className="my-8 rounded-lg border-2 border-red-600/60 bg-gradient-to-b from-red-950/40 to-black/95 shadow-[0_0_40px_rgba(220,38,38,0.25)] p-6 sm:p-8"
    >
      <div className="flex items-center gap-2 text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-4">
        <Lock className="h-3.5 w-3.5" />
        Members Only
      </div>
      <h2
        className="text-white text-2xl sm:text-3xl font-black leading-tight mb-4"
        style={{ fontFamily: 'Georgia, serif', letterSpacing: '0.01em' }}
      >
        Read this one on us. Make a free account and get a story every month.
      </h2>
      <p className="text-zinc-400 text-sm mb-6">
        One free story a month, no card, just an email. Want the lot &mdash; every investigation, the
        Rumour File, the Gangland Map and the videos? Monthly $13 · Annual $130 · Founding $249
        one-off (80 places).
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          to={appendUtm("/login")}
          data-testid="paywall-free-cta"
          className="inline-flex items-center justify-center px-5 py-3 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.25em] transition-colors"
        >
          Read free with an account
        </Link>
        <Link
          to={appendUtm("/subscribe")}
          data-testid="paywall-signup-cta"
          className="inline-flex items-center justify-center px-5 py-3 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-bold text-xs uppercase tracking-[0.25em] transition-colors"
        >
          Go unlimited
        </Link>
      </div>
    </aside>
  );
};

export default PaywallCard;
